import { routeNames } from '@core/routeNames';
import { RestaurantSegmentEnum } from '@enums';
import { getFormattedThousand } from '@helpers';
import { SavedSelection } from '@pages/Home/context/TerritorySelectionContext';
import { RestaurantSegmentOption } from '@apiHooks/useRestaurantSegmentOptions';
import { RESTAURANT_SEGMENT_LABELS } from '@pages/Home/sections/HomePlaces/constants';

export function buildMoreChainTargetsUrl(
    segments: RestaurantSegmentEnum[],
    savedSelection: SavedSelection | null,
): string {
    const params = new URLSearchParams();

    if (segments.length > 0) {
        params.set('segments', segments.join(','));
    }

    if (savedSelection && savedSelection.ids.length > 0) {
        const key = savedSelection.mode === 'state' ? 'states' : 'metros';
        params.set(key, savedSelection.ids.join(','));
    }

    const qs = params.toString();
    return qs ? `${routeNames.chains}?${qs}` : routeNames.chains;
}

export function buildChainProfileUrl(id: number): string {
    return `${routeNames.chainDetails}?id=${id}`;
}

export function formatUnitGrowth(val: number): string {
    const sign = val > 0 ? '+' : val < 0 ? '-' : '';
    return `${sign}${getFormattedThousand(Math.abs(val))}`;
}

export function formatSalesGrowth(val: number): string {
    const sign = val > 0 ? '+' : val < 0 ? '-' : '';
    // toFixed first so the decimal rounds correctly, then comma-separate the whole part
    const [whole, decimal] = Math.abs(val).toFixed(1).split('.');
    return `${sign}${getFormattedThousand(Number(whole))}.${decimal}%`;
}

export function buildMoreRestaurantsUrl(
    independentsUrl: string | null,
    segments: RestaurantSegmentEnum[],
    segmentOptions: RestaurantSegmentOption[],
): string | null {
    if (!independentsUrl) return null;

    const segmentIds = segments
        .map(seg => {
            const label = RESTAURANT_SEGMENT_LABELS[seg];
            return segmentOptions.find(o => o.name === label)?.id;
        })
        .filter((id): id is number => id !== undefined);

    if (!segmentIds.length) return independentsUrl;

    // BE returns URL with `segment=` already present but empty — replace it in place
    if (/[?&]segment=/.test(independentsUrl)) {
        return independentsUrl.replace(/([?&]segment=)[^&]*/, `$1${segmentIds.join(',')}`);
    }
    const sep = independentsUrl.includes('?') ? '&' : '?';
    return `${independentsUrl}${sep}segment=${segmentIds.join(',')}`;
}

export function buildMoreOnSiteTargetsUrl(
    segments: string[],
    savedSelection: SavedSelection | null,
): string {
    const params = new URLSearchParams();

    if (segments.length > 0) {
        params.set('onSiteSegments', segments.join(','));
    }

    if (savedSelection && savedSelection.ids.length > 0) {
        const key = savedSelection.mode === 'state' ? 'states' : 'metros';
        params.set(key, savedSelection.ids.join(','));
    }

    const qs = params.toString();
    return qs ? `${routeNames.chains}?${qs}` : routeNames.chains;
}
